import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { darkTheme, lightTheme } from './corporateTheme';
import { DARK, LIGHT } from './claudeTokens';

const STORAGE_KEY = 'odin1-theme-mode';

// The html attribute ClaudeVars keys the dark variable block on. Set
// synchronously so the first paint after hydration already matches (the
// pre-paint script in index.html covers the paint before that).
const stampMode = (isDarkMode) => {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = isDarkMode ? 'dark' : 'light';
  // Browser/OS chrome tint (index.html ships the light value for first paint).
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', isDarkMode ? DARK.bg : LIGHT.bg);
};

// Create theme context with toggle function
const ThemeContext = createContext({
  theme: lightTheme,
  isDarkMode: false,
  toggleTheme: () => {},
});

// Theme provider component with persistence
export const ThemeProvider = ({ children }) => {
  // Initialize from localStorage, default to light mode (spec 2026-09-24 §6)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check if running in browser
    if (typeof window === 'undefined') return false;

    const saved = localStorage.getItem(STORAGE_KEY);
    const dark = saved !== null ? saved === 'dark' : false; // Default to light
    stampMode(dark);
    return dark;
  });

  // Get current theme based on mode
  const theme = useMemo(() => (isDarkMode ? darkTheme : lightTheme), [isDarkMode]);

  // Persist theme preference to localStorage and keep the html attribute in step
  useEffect(() => {
    stampMode(isDarkMode);
    localStorage.setItem(STORAGE_KEY, isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Toggle between dark and light mode
  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ theme, isDarkMode, toggleTheme }),
    [theme, isDarkMode]
  );

  return (
    <StyledThemeProvider theme={theme}>
      <ThemeContext.Provider value={contextValue}>
        {children}
      </ThemeContext.Provider>
    </StyledThemeProvider>
  );
};

// Hook to use theme and toggle
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
